'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useI18n } from '@/lib/i18n'
import { gameQueries } from '@/lib/queries'
import { Loader2, Plus, Wallet } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

type Topup = {
  id: string
  email: string
  delta: number
  note: string
  createdAt: string
}

function formatCredits(microdollars: number) {
  const sign = microdollars < 0 ? '-' : '+'
  return `${sign}$${Math.abs(microdollars / 1_000_000).toFixed(2)}`
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

export function TopupSection() {
  const { t } = useI18n()
  const [topups, setTopups] = useState<Topup[]>([])
  const [email, setEmail] = useState('')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const loadTopups = useCallback(async () => {
    setLoading(true)
    try {
      const result = await gameQueries.getManualTopups()
      if (result.success && result.data)
        setTopups(result.data)
    }
    catch (error) {
      console.error('Failed to load top-ups:', error)
      setMessage({ type: 'error', text: 'Failed to load top-ups' })
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTopups()
  }, [loadTopups])

  const trimmedEmail = email.trim()
  const amountDollars = Number.parseFloat(amount)
  const amountValid = !!trimmedEmail && Number.isFinite(amountDollars) && amountDollars !== 0

  const handleRequestSubmit = () => {
    if (!amountValid) {
      setMessage({ type: 'error', text: 'Enter an email and a non-zero amount.' })
      return
    }
    setMessage(null)
    setConfirmOpen(true)
  }

  const handleSubmit = async () => {
    if (!amountValid)
      return
    setConfirmOpen(false)
    setSubmitting(true)
    setMessage(null)
    try {
      const result = await gameQueries.topUpCredit({
        email: trimmedEmail,
        amountDollars,
        note: note.trim() || undefined,
      })
      setEmail('')
      setAmount('')
      setNote('')
      await loadTopups()
      setMessage({
        type: 'success',
        text: `${result.message}. New balance: $${(result.newBank / 1_000_000).toFixed(2)}`,
      })
    }
    catch (error) {
      console.error('Failed to top up credit:', error)
      const text = error instanceof Error ? error.message : 'Failed to top up credit'
      setMessage({ type: 'error', text })
    }
    finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            {t('admin.nav.topup', 'Top-Up Credit')}
          </h2>
          <p className="text-sm text-muted-foreground">
            Add or adjust credit for a user by email. This writes a CreditLedger entry, so Credit
            Reconciliation counts it as legitimate and never wipes it. Amounts are in US dollars;
            negative values deduct.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="size-5" />
              Manual Top-Ups ({topups.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {message && (
              <p
                className={`text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}
              >
                {message.text}
              </p>
            )}

            { }
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="user@example.com"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="max-w-[260px]"
              />
              <Input
                placeholder="Amount (USD)"
                type="number"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="max-w-[140px]"
              />
              <Input
                placeholder="Note (optional)"
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRequestSubmit()}
                className="max-w-[220px]"
              />
              <Button
                onClick={handleRequestSubmit}
                disabled={submitting || !email.trim() || !amount.trim()}
                size="sm"
                className="gap-1"
              >
                {submitting ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                Add Credit
              </Button>
            </div>

            { }
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading...
              </div>
            ) : topups.length > 0 ? (
              <div className="space-y-1">
                { }
                <div className="grid grid-cols-[1fr_110px_1fr_160px] items-center gap-2 border-b px-2 pb-2 text-xs font-medium text-muted-foreground">
                  <span>User</span>
                  <span className="text-right">Amount</span>
                  <span>Note</span>
                  <span className="text-right">When</span>
                </div>
                {topups.map(tu => (
                  <div
                    key={tu.id}
                    className="grid grid-cols-[1fr_110px_1fr_160px] items-center gap-2 rounded-md p-2 transition-colors hover:bg-muted/50"
                  >
                    <span className="truncate text-sm font-medium">{tu.email}</span>
                    <span
                      className={`text-right text-sm font-medium tabular-nums ${tu.delta < 0 ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}
                    >
                      {formatCredits(tu.delta)}
                    </span>
                    <span className="truncate text-sm text-muted-foreground">{tu.note || '—'}</span>
                    <span className="text-right text-xs tabular-nums text-muted-foreground">{formatDate(tu.createdAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No manual top-ups yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {
      }
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg border bg-background p-6">
            <h3 className="mb-3 text-lg font-semibold">Confirm credit adjustment</h3>
            <p className="mb-2 text-sm text-muted-foreground">
              {amountDollars >= 0 ? 'Credit' : 'Deduct'}
              {' '}
              <strong className={amountDollars >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}>
                {amountDollars >= 0 ? '+' : '-'}
                $
                {Math.abs(amountDollars).toFixed(2)}
              </strong>
              {' '}
              {amountDollars >= 0 ? 'to' : 'from'}
              {' '}
              <strong>{trimmedEmail}</strong>
              {note.trim() ? ` (note: ${note.trim()})` : ''}
              .
            </p>
            {Math.abs(amountDollars) > 100 && (
              <p className="mb-2 text-sm font-medium text-red-600 dark:text-red-400">
                Large amount — double-check the value.
              </p>
            )}
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="gap-2">
                {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
